import { Text, Title } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import {
  InsightQuickCaptureModal,
  NoteQuickCaptureModal,
  QuickThoughtsActions,
} from '@/components/shared/QuickThoughts';
import Loader from '@/components/ui/Loader';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { useLocalStore } from '@/hooks/useLocalStore';
import { notificationsStore } from '@/stores/global/RootStore';
import {
  OrganizationNewsPageStore,
  OrganizationNewsPageStoreProvider,
} from '@/stores/local/pages/OrganizationNewsPageStore';
import { useDevices } from '@/utils/devices';

import MobileQuickThoughtsActions from './MobileQuickThoughtsActions';

import s from './NewsDetail.module.scss';

const NewsDetail: React.FC = () => {
  const { isCompactViewport } = useDevices();
  const { id, newsId } = useParams();

  const store = useLocalStore(
    () =>
      new OrganizationNewsPageStore({
        organizationId: Number(id),
        activeId: Number(newsId),
        notificationsStore,
      }),
  );

  React.useEffect(() => {
    store.setActiveId(Number(newsId));
  }, [store, newsId]);

  const handleCreateNoteClick = React.useCallback(() => {
    const selection = window.getSelection()?.toString() ?? '';

    if (!id || !newsId) {
      store.quickThoughts.note.openWithSelection(selection);

      return;
    }

    store.quickThoughts.note.openWithSelection(selection, {
      createPayload: {
        artifact_type: 'news',
        artifact_id: newsId,
      },
    });
  }, [id, newsId, store]);

  const handleCreateInsightClick = React.useCallback(() => {
    const selection = window.getSelection()?.toString() ?? '';

    store.quickThoughts.insight.openWithSelection(selection);
  }, [store]);

  if (store.meta.isLoading) {
    return <Loader fullHeight type="dots" size="xl" />;
  }

  return (
    <OrganizationNewsPageStoreProvider store={store}>
      <div className={s.root}>
        {store.newsSummary?.publishDate && !isCompactViewport && (
          <Text className={s.root__date} size="xs" lh="xxs" c="dimmed">
            {store.newsSummary?.publishDate}
          </Text>
        )}
        {store.newsSummary?.source && (
          <Text size={isCompactViewport ? 'xs' : 'md'} className={s.root__source}>
            Источник:{' '}
            <Text
              size={isCompactViewport ? 'xs' : 'md'}
              className={s.root__link}
              href={store.newsSummary.source}
              target="_blank"
              component="a"
              c="blue"
            >
              {store.newsSummary.source}
            </Text>
          </Text>
        )}
        {!isCompactViewport && (
          <div className={s['root__actions-wrapper']}>
            <QuickThoughtsActions
              className={s['root__actions']}
              onCreateNote={handleCreateNoteClick}
              onCreateInsight={handleCreateInsightClick}
              disableNote={store.meta.isLoading}
              disableInsight={store.meta.isLoading}
            />
          </div>
        )}
        {store.newsSummary?.title && (
          <Title className={s.root__title} component="h3" size={isCompactViewport ? 'h5' : 'h3'} fw={700}>
            {store.newsSummary?.title}
          </Title>
        )}
        {store.newsSummary?.summary && (
          <MarkdownRenderer className={s.root__summary} content={store.newsSummary?.summary} />
        )}
        <MarkdownRenderer className={s.root__content} content={store.newsSummary?.importanceExplanation} />
        {store.newsSummary?.text && <MarkdownRenderer className={s.root__text} content={store.newsSummary?.text} />}
      </div>
      {isCompactViewport && (
        <MobileQuickThoughtsActions
          onCreateNote={handleCreateNoteClick}
          onCreateInsight={handleCreateInsightClick}
          disableNote={store.meta.isLoading}
          disableInsight={store.meta.isLoading}
        />
      )}
      <NoteQuickCaptureModal store={store.quickThoughts.note} />
      <InsightQuickCaptureModal store={store.quickThoughts.insight} />
    </OrganizationNewsPageStoreProvider>
  );
};

export default observer(NewsDetail);
